// dnd-kit requires every draggable/droppable `id` to be unique within one
// DndContext - but blocks and containers live in separate DB tables with
// independent auto-increment sequences (3 tables on the exercise side:
// static/interactive/container), so a block and a container CAN share a
// raw numeric id. Namespacing avoids that collision - reuses the exact
// convention already used for React `key`s in both constructors ('block-'
// + id, 'container-' + id, entry.kind + '-' + block.id), just promoted to
// also be the dnd-kit id, not just a render key.
export function makeDndId(kind, id) {
   return kind + '-' + id;
}

// A droppable-only id for a container's empty-state region - never a drag
// SOURCE (nothing has this id as its own draggable id), only ever a valid
// drop TARGET when a container has zero children to otherwise anchor a
// SortableContext to. Deliberately NOT prefixed "container-" (even though
// conceptually it belongs to a container) - parseDndId splits on the FIRST
// dash, so "container-empty-5" would parse to the exact same kind
// ("container") as the container's own top-level slot id ("container-5").
// That collision was a real bug: collisionDetection.js's "exclude
// container-kind whenever a more specific candidate exists" filter ended
// up excluding the empty-dropzone too, since it couldn't tell the two
// apart - a distinct, dash-free kind prefix avoids that ambiguity outright.
export function makeEmptyDropzoneId(containerId) {
   return 'containerEmpty-' + containerId;
}

// A droppable-only id for "the tail end of the top-level list, after its
// last item" - the region the add-trigger row occupies. Ejecting a block
// back to top-level (dropping it outside every container) needs an
// explicit, always-registered target here for the same reason the empty-
// container dropzone above needs one: without it, hovering that trailing
// area has no droppable of its own to match, so collision detection falls
// back to whatever ELSE geometrically overlaps nearby - usually the last
// container's own slot - which (correctly, for a genuine hover of that
// container) now redirects into that container, silently swallowing what
// was meant to be an eject-to-top-level drop instead.
export const TOP_LEVEL_END_ID = 'topLevelEnd';

// Opaque split, no entity-shape awareness - kind is whatever string the
// caller used with makeDndId (e.g. 'block'/'container' on the Topic side,
// 'static'/'interactive'/'container' on the Exercise side).
export function parseDndId(dndId) {
   const sepIndex = dndId.indexOf('-');
   if (sepIndex === -1) return { kind: dndId, id: null };
   return { kind: dndId.slice(0, sepIndex), id: dndId.slice(sepIndex + 1) };
}
